import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LoginIcon from '@mui/icons-material/Login';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { apiFetch } from '../api/api';
import MessageModal from '../components/MessageModal';
import {
  clearAuthSession,
  getApiErrorMessage,
  getAuthToken,
  getStoredUser,
  isUserLoggedIn,
  readJsonSafe,
  saveAuthSession,
} from '../utils/authSession';

const PROFILE_GET_ENDPOINT = '/users/auth/me';
const PROFILE_UPDATE_ENDPOINT = '/users/auth/profile';

const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(184,137,74,0.06)',
    borderColor: 'divider',
    overflow: 'hidden',
  },
};

const primaryButtonSx = {
  py: 1.5,
  borderRadius: 2.5,
  fontSize: '1rem',
  fontWeight: 800,
  background: 'linear-gradient(90deg, #D4A574, #B8894A)',
  boxShadow: '0 6px 18px rgba(184,137,74,0.28)',
  '&:hover': {
    background: 'linear-gradient(90deg, #B8894A, #8F6A35)',
  },
};

const outlinedButtonSx = {
  py: 1.25,
  borderRadius: 2.5,
  fontWeight: 700,
  borderColor: alpha('#B8894A', 0.35),
  color: 'primary.main',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: alpha('#B8894A', 0.06),
  },
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    bgcolor: 'background.paper',
  },
};

function PageTitle({ title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.5 } }}>
      <Box
        sx={{
          width: 5,
          height: { xs: 28, md: 34 },
          borderRadius: 999,
          flexShrink: 0,
          background: 'linear-gradient(180deg, #D4A574, #B8894A)',
          boxShadow: '0 2px 10px rgba(184,137,74,0.28)',
        }}
      />
      <Box>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.45rem', md: '1.75rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '0.95rem' }}>
            {subtitle}
          </Typography>
        ) : null}
        <Box
          sx={{
            mt: 0.75,
            width: { xs: 40, md: 52 },
            height: 3,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #D4A574, #B8894A)',
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
}

function extractUser(json) {
  return json?.user ?? json?.data?.user ?? json?.data ?? json;
}

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loggedIn, setLoggedIn] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [messageModal, setMessageModal] = useState({
    visible: false,
    title: '',
    message: '',
    variant: 'info',
  });

  const showMessage = useCallback((title, message, variant = 'error') => {
    setMessageModal({ visible: true, title, message, variant });
  }, []);

  const closeMessage = useCallback(() => {
    setMessageModal((prev) => ({ ...prev, visible: false }));
  }, []);

  const loadProfile = useCallback(async () => {
    const isLogged = await isUserLoggedIn();
    setLoggedIn(isLogged);
    if (!isLogged) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(PROFILE_GET_ENDPOINT);
      const json = await readJsonSafe(response);

      if (!response.ok) {
        const message = await getApiErrorMessage(response, t('profile_load_failed'));
        showMessage(t('login_error'), message);
        const stored = await getStoredUser();
        if (stored) {
          setName(stored.name ?? '');
          setPhone(stored.phone ?? stored.number ?? '');
        }
        return;
      }

      const user = extractUser(json);
      setName(user?.name ?? '');
      setPhone(user?.phone ?? user?.number ?? '');
    } catch {
      showMessage(t('login_error'), t('profile_load_failed'));
      const stored = await getStoredUser();
      if (stored) {
        setName(stored.name ?? '');
        setPhone(stored.phone ?? stored.number ?? '');
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showMessage(t('login_error'), t('login_name_required'));
      return;
    }

    setSaving(true);
    try {
      const response = await apiFetch(PROFILE_UPDATE_ENDPOINT, {
        method: 'PUT',
        body: JSON.stringify({ name: trimmedName }),
      });

      const json = await readJsonSafe(response);
      if (!response.ok) {
        const message = await getApiErrorMessage(response, t('profile_update_failed'));
        showMessage(t('login_error'), message);
        return;
      }

      const user = extractUser(json);
      const token = await getAuthToken();
      const updatedUser = {
        ...user,
        name: user?.name ?? trimmedName,
        phone: user?.phone ?? user?.number ?? phone,
      };

      await saveAuthSession({
        token: token ?? undefined,
        user: updatedUser,
      });

      setName(updatedUser.name ?? trimmedName);
      window.dispatchEvent(new Event('profile-updated'));
      showMessage(t('profile_update_success'), t('profile_update_success_message'), 'success');
    } catch {
      showMessage(t('login_error'), t('profile_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await clearAuthSession();
    setLoggedIn(false);
    setName('');
    setPhone('');
    window.dispatchEvent(new Event('profile-updated'));
    navigate('/', { replace: true });
  };

  const displayName = name.trim() || t('Account');
  const avatarLetter = displayName.charAt(0).toUpperCase();

  return (
    <Box sx={{ bgcolor: 'background.default', pb: { xs: 4, md: 6 }, minHeight: '60vh' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 1.5,
              '& .MuiBreadcrumbs-li': { fontSize: '0.9rem' },
              '& .MuiLink-root:hover': { color: 'primary.main' },
            }}
          >
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={700}>
              {t('Profile')}
            </Typography>
          </Breadcrumbs>
          <PageTitle
            title={t('Profile')}
            subtitle={loggedIn === false ? t('Login') : t('Welcome')}
          />
        </Container>
      </Box>

      <Container maxWidth="sm">
        {loggedIn === false ? (
          <Paper
            {...pagePaperSx}
            sx={{ ...pagePaperSx.sx, p: { xs: 4, md: 6 }, textAlign: 'center' }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                mx: 'auto',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#B8894A', 0.08),
                border: 1,
                borderColor: alpha('#B8894A', 0.14),
                boxShadow: '0 8px 24px rgba(184,137,74,0.12)',
              }}
            >
              <LoginIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={800} color="primary.main" gutterBottom>
              {t('Login')}
            </Typography>
            <Typography color="text.secondary" fontWeight={600} sx={{ mb: 3 }}>
              {t('favourites_login_required')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                component={RouterLink}
                to="/signin"
                variant="contained"
                size="large"
                startIcon={<LoginIcon />}
                sx={{ px: 3, ...primaryButtonSx, py: 1.25 }}
              >
                {t('Login')}
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="outlined"
                size="large"
                sx={{ px: 3, ...outlinedButtonSx }}
              >
                {t('Register')}
              </Button>
            </Stack>
          </Paper>
        ) : loading ? (
          <Paper {...pagePaperSx} sx={{ ...pagePaperSx.sx, p: 4 }}>
            <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Skeleton variant="circular" width={88} height={88} />
              <Skeleton variant="text" width="60%" height={32} />
            </Stack>
            <Skeleton variant="rounded" height={56} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={56} sx={{ mt: 2, borderRadius: 3 }} />
          </Paper>
        ) : (
          <Stack spacing={2.5}>
            <Paper {...pagePaperSx}>
              <Box
                sx={{
                  px: { xs: 3, sm: 4 },
                  py: 4,
                  textAlign: 'center',
                  background:
                    'linear-gradient(180deg, rgba(212,165,116,0.12) 0%, rgba(184,137,74,0.04) 100%)',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  sx={{
                    width: 88,
                    height: 88,
                    mx: 'auto',
                    mb: 2,
                    fontSize: 36,
                    fontWeight: 800,
                    bgcolor: 'primary.main',
                    boxShadow: '0 8px 24px rgba(184,137,74,0.28)',
                  }}
                >
                  {avatarLetter}
                </Avatar>
                <Typography variant="h5" fontWeight={800} color="text.primary">
                  {displayName}
                </Typography>
                {phone && (
                  <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>
                    {phone}
                  </Typography>
                )}
              </Box>

              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label={t('login_full_name')}
                    placeholder={t('login_full_name_placeholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={textFieldSx}
                    InputProps={{
                      startAdornment: <PersonOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                    }}
                  />

                  {phone && (
                    <TextField
                      fullWidth
                      label={t('login_phone')}
                      value={phone}
                      disabled
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: <PhoneOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={<SaveOutlinedIcon />}
                    sx={primaryButtonSx}
                  >
                    {saving ? '...' : t('profile_save')}
                  </Button>
                </Stack>
              </Box>
            </Paper>

            <Paper {...pagePaperSx} sx={{ ...pagePaperSx.sx, p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  component={RouterLink}
                  to="/orders"
                  variant="outlined"
                  fullWidth
                  startIcon={<ReceiptLongOutlinedIcon />}
                  sx={outlinedButtonSx}
                >
                  {t('orders.header')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/favourites"
                  variant="outlined"
                  fullWidth
                  startIcon={<FavoriteBorderIcon />}
                  sx={outlinedButtonSx}
                >
                  {t('Favourites')}
                </Button>
              </Stack>
            </Paper>

            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                borderRadius: 2.5,
                py: 1.25,
                fontWeight: 700,
                borderColor: alpha('#d32f2f', 0.35),
                bgcolor: alpha('#d32f2f', 0.04),
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: alpha('#d32f2f', 0.08),
                },
              }}
            >
              {t('logout')}
            </Button>
          </Stack>
        )}
      </Container>

      <MessageModal
        visible={messageModal.visible}
        onClose={closeMessage}
        title={messageModal.title}
        message={messageModal.message}
        variant={messageModal.variant}
      />
    </Box>
  );
}
